import { KeyboardEvent, useState } from 'react'
import { Box, Button, Stack, TextField, Typography } from '@mui/material'
import { useStytchMember } from '@stytch/react/b2b'
import { QueryKey } from '@tanstack/react-query'
import { useCreateDocumentComment } from '@/service-library/hooks/document-comments'
import { DocComment } from '@/types/comments'
import { showErrorSnackbar } from '@/utils/snackbars'
import generateUuid from '@/utils/uuid'

const MAX_LENGTH = 1000

/**
 * Caps the raw draft so its *trimmed* length never exceeds MAX_LENGTH —
 * slicing on raw length alone could stop short of the limit whenever the
 * draft has leading/trailing whitespace, leaving the counter and the actual
 * cutoff out of sync.
 */
function clampToTrimmedLimit(value: string): string {
  if (value.trim().length <= MAX_LENGTH) return value
  const leadingWhitespace = value.length - value.trimStart().length
  return value.slice(0, leadingWhitespace + MAX_LENGTH)
}

type CommentComposerProps = {
  documentId: string
  /** Must match the query key CommentsDrawer uses to fetch the list, so the
   * optimistic comment lands in the same cached page. */
  listQueryKey: QueryKey
  /** The count badge's query key, invalidated on post so the badge count stays
   * in sync with the list. */
  countQueryKey?: QueryKey
}

export default function CommentComposer({
  documentId,
  listQueryKey,
  countQueryKey,
}: CommentComposerProps) {
  const [draft, setDraft] = useState('')
  const { member } = useStytchMember()

  const { createDocumentComment } = useCreateDocumentComment({
    listQueryKey,
    sideEffectQueryKeys: [countQueryKey],
    onError: () => {
      showErrorSnackbar("Couldn't post your comment. Please try again.")
    },
  })

  const trimmed = draft.trim()
  const atLimit = trimmed.length >= MAX_LENGTH
  const postDisabled = trimmed.length === 0

  function handleSubmit() {
    if (postDisabled || !member) return

    // Client-generated id (repo convention, see src/utils/uuid.ts) lets
    // useCreateDetail optimistically append this exact record to the
    // cached list immediately, and revert it automatically if the POST
    // fails.
    const newComment: DocComment = {
      id: generateUuid(),
      document_id: documentId,
      body: trimmed,
      created_at: new Date().toISOString(),
      created_by_user_id: member.external_id as string,
      deleted_at: null,
    }

    setDraft('')
    createDocumentComment(newComment)
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault()
      handleSubmit()
    }
  }

  return (
    <Box
      sx={{
        borderTop: (theme) => `1px solid ${theme.palette.divider}`,
        p: 2,
      }}
    >
      <TextField
        variant="outlined"
        multiline
        minRows={4}
        maxRows={12}
        fullWidth
        placeholder="Add a comment…"
        value={draft}
        onChange={(event) => setDraft(clampToTrimmedLimit(event.target.value))}
        onKeyDown={handleKeyDown}
      />
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 1 }}>
        {atLimit && (
          <Typography variant="caption" color="error" sx={{ fontWeight: 500 }}>
            Maximum reached
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <Typography
          variant="caption"
          color={
            atLimit
              ? 'error'
              : trimmed.length >= MAX_LENGTH * 0.9
              ? 'warning.main'
              : 'text.secondary'
          }
        >
          {trimmed.length.toLocaleString()} / {MAX_LENGTH.toLocaleString()}
        </Typography>
        <Button
          variant="contained"
          size="small"
          onClick={handleSubmit}
          disabled={postDisabled}
        >
          Post
        </Button>
      </Stack>
    </Box>
  )
}
